/* eslint-disable react/jsx-props-no-spreading */
import React, { useId } from 'react';
import { twMerge } from 'utilities/src/twMerge';

import { IInputTextProps } from './_props';

import { TFormField } from '../../shared/utils/types';

import { LabelTop, MessageError, MessageHelper, Field } from '../../shared/components';

import useInputTextTheme from './themes';
import LabelAdornmentWrapper from './_LabelAdornmentWrapper';

function InputText<TFormFields extends TFormField = TFormField>({
  name,
  label = '',
  labelPosition = 'Top',
  errorMessage = '',
  errorIcon = undefined,
  errorClassName = '',
  helperMessage = '',
  placeholder = '',
  type = 'text',
  state = 'Default',
  theme = 'Light',
  variant = 'Secondary',
  size = 'Medium',
  icon = undefined,
  stateIcon = undefined,
  iconPosition = 'left',
  required = false,
  value = '',
  className = '',
  fieldClassName = '',
  register,
  rules,
  onBlur,
  onChange,
  onKeyDown,
  maxLength,
  tabIndex,
  id,
  errorSpace = false,
  labelClassName,
  labelAdornment,
}: IInputTextProps<TFormFields>) {
  const { inputTextWrapperCommonClasses, inputLabelWrapperThemeClasses, inputTextCommonClasses } =
    useInputTextTheme({ inputState: state });

  const useid = useId();
  const uid = id ?? useid;
  const hasTopLabelAdornment = !!labelAdornment && labelPosition === 'Top';

  return (
    <div className={twMerge(inputTextWrapperCommonClasses, className)}>
      <LabelAdornmentWrapper
        className={inputLabelWrapperThemeClasses}
        htmlFor={uid}
        labelAdornment={hasTopLabelAdornment ? labelAdornment : undefined}
        LabelComponent={
          <LabelTop
            size={size}
            id={uid}
            label={label}
            labelPosition={labelPosition}
            theme={theme}
            required={required}
            labelClassName={labelClassName}
            htmlFor={hasTopLabelAdornment ? uid : undefined}
          />
        }
      >
        <Field
          id={uid}
          type="Input"
          state={state}
          theme={theme}
          variant={variant}
          size={size}
          label={label}
          labelPosition={labelPosition}
          value={value}
          icon={icon}
          stateIcon={stateIcon}
          iconPosition={iconPosition}
          required={required}
          className={fieldClassName}
        >
          <input
            className={inputTextCommonClasses}
            type={type}
            name={name}
            aria-label={name}
            id={uid}
            value={value}
            onBlur={onBlur}
            onChange={onChange}
            onKeyDown={onKeyDown}
            maxLength={maxLength}
            tabIndex={tabIndex}
            {...(labelPosition === 'Top' && { placeholder })}
            {...(register &&
              register(name, {
                onChange,
                onBlur,
                ...rules,
              }))}
          />
        </Field>
      </LabelAdornmentWrapper>

      <MessageError
        message={errorMessage}
        icon={errorIcon}
        state={state}
        theme={theme}
        className={errorClassName}
        errorSpace={errorSpace}
      />

      <MessageHelper message={helperMessage} state={state} theme={theme} />
    </div>
  );
}

export default InputText;
